import {
  Menu,
  X,
  ChevronDown,
  FileText,
  Building2,
  Users,
  MapPin,
  Phone,
  Mail,
  AlertCircle,
  CheckCircle,
  Send,
  TrendingUp,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { notify } from "@/components/common/Notification";
import logoImage from "../assets/JBF logo.png";
import { usePrefixedNavigate } from "@/projects/Jainboarding_Admin/hooks/usePrefixedNavigate";

interface NavbarProps {
  currentPage: string;
}

const USER_BASE_PATH = "/";
const pagePathMap: Record<string, string> = {
  home: USER_BASE_PATH,
  about: "/about",
  committee: "/committee",
  programs: "/programs",
  governance: "/governance",
  platform: "/platform",
  "hostel-directory": "/hostel-directory",
  donors: "/donors",
  vendors: "/vendors",
  knowledge: "/knowledge-hub",
  blogs: "/blogs",
  contact: "/contact",
  "impact-transparency": "/impact-transparency",
  events: "/events",
  "media-gallery": "/media-gallery",
};

export default function Navbar({ currentPage }: NavbarProps) {
  const routeNavigate = useNavigate();
  const navigate = usePrefixedNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const navItemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [hostelFormData, setHostelFormData] = useState({
    hostelName: "",
    trustName: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    contactPerson: "",
    phone: "",
    email: "",
    capacity: "",
    currentOccupancy: "",
  });

  const navItems = [
    { id: "home", label: "Home" },
    {
      id: "about",
      label: "About JBF",
      hasDropdown: true,
      dropdownItems: [
        { id: "committee", label: "Committee" },
        { id: "governance", label: "Governance Model" },
        { id: "events", label: "Events" },
        { id: "media-gallery", label: "Media Gallery" },
        // { id: "hostel-directory", label: "Hostel Directory" },
      ],
    },
    { id: "programs", label: "Programs & Initiatives" },
    { id: "platform", label: "Digital Platform" },
    { id: "hostel-directory", label: "Institution Directory" },
    { id: "donors", label: "Donors & CSR" },
    { id: "vendors", label: "Vendors & Experts" },
    {
      id: "blogs",
      label: "Blogs",
      // hasDropdown: true,
      // dropdownItems: [{ id: "blogs", label: "Blogs" }],
    },
    { id: "contact", label: "Contact / Registration" },
  ];

  const handleNavClick = (pageId: string) => {
    routeNavigate(pagePathMap[pageId] ?? USER_BASE_PATH);
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleProceedToForm = () => {
    setShowRegistrationModal(false);
    navigate("/auth?portal=institution");
    // setShowRegistrationForm(true);
  };

  const handleHostelFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setHostelFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);
      // Here you would typically send the data to your API
      console.log("Hostel Registration Data:", hostelFormData);

      notify.success(
        "Registration Submitted Successfully",
        "We'll contact you soon. Thank you for registering!"
      );
      setShowRegistrationForm(false);
      // Reset form after successful submission
      setHostelFormData({
        hostelName: "",
        trustName: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        contactPerson: "",
        phone: "",
        email: "",
        capacity: "",
        currentOccupancy: "",
      });
    } catch (error: any) {
      console.error("Form submission error:", error);
      notify.error(
        "Registration Failed",
        error?.message ?? "Unable to submit registration. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const updateDropdownPosition = () => {
      if (activeDropdown) {
        const navItem = navItemRefs.current[activeDropdown];
        if (navItem) {
          const rect = navItem.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom - 0.5, // mt-1 = 4px
            left: rect.left - 2,
          });
        }
      } else {
        setDropdownPosition(null);
      }
    };

    updateDropdownPosition();

    if (activeDropdown) {
      window.addEventListener("resize", updateDropdownPosition);
      window.addEventListener("scroll", updateDropdownPosition, true);

      return () => {
        window.removeEventListener("resize", updateDropdownPosition);
        window.removeEventListener("scroll", updateDropdownPosition, true);
      };
    }
  }, [activeDropdown]);

  useEffect(() => {
    if (showRegistrationModal || showRegistrationForm) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showRegistrationModal, showRegistrationForm]);

  return (
    <nav className="sticky top-0 z-[100] bg-white shadow-sm">
      {/* Top Bar - Logo and CTA Buttons */}
      <div className="border-b border-gray-100">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="flex h-20 items-center justify-between">
            {/* Logo */}
            <div
              className="group flex cursor-pointer items-center"
              onClick={() => handleNavClick("home")}
            >
              <img
                src={logoImage}
                alt="JBF Logo"
                className="h-14 w-auto transition-transform group-hover:scale-105"
              />
            </div>

            {/* CTA Buttons - Desktop */}
            <div className="hidden items-center space-x-3 lg:flex">
              <button
                onClick={() => setShowRegistrationModal(true)}
                className="navbar-link rounded-lg border-2 border-[#7A1E1E] px-6 py-2.5 text-[#7A1E1E] transition-all hover:bg-[#7A1E1E] hover:text-white"
              >
                Register Institution
              </button>
              <button
                onClick={() => handleNavClick("donors")}
                className="navbar-link transform rounded-lg bg-gradient-to-r from-[#D4A017] to-[#B8900F] px-6 py-2.5 text-white transition-all hover:-translate-y-0.5 hover:shadow-lg"
              >
                Donor Portal
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="rounded-md p-2 text-[#7A1E1E] hover:bg-[#FFF5E6] lg:hidden"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Bar - Full Width */}
      <div className="navbar-custom-gradient hidden overflow-x-auto border-b border-gray-200 bg-[#FFF9F0] lg:block">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-start">
            <div className="relative flex items-center space-x-1 py-3">
              {navItems.map(item => (
                <div
                  key={item.id}
                  ref={el => {
                    if (item.hasDropdown) {
                      navItemRefs.current[item.id] = el;
                    }
                  }}
                  className="relative"
                  onMouseEnter={() =>
                    item.hasDropdown && setActiveDropdown(item.id)
                  }
                  onMouseLeave={() =>
                    item.hasDropdown && setActiveDropdown(null)
                  }
                >
                  <button
                    onClick={() => handleNavClick(item.id)}
                    className={`navbar-link group relative flex items-center gap-1 whitespace-nowrap px-3 py-2 text-sm transition-all ${
                      currentPage === item.id
                        ? "text-[#7A1E1E]"
                        : "text-gray-700 hover:text-[#7A1E1E]"
                    }`}
                  >
                    {item.label}
                    {item.hasDropdown && <ChevronDown className="h-4 w-4" />}
                    <span
                      className={`absolute bottom-0 left-0 h-0.5 w-full transform bg-[#D4A017] transition-transform ${
                        currentPage === item.id
                          ? "scale-x-100"
                          : "scale-x-0 group-hover:scale-x-100"
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Dropdown Menu - rendered outside overflow context with fixed positioning */}
      {activeDropdown && dropdownPosition && (
        <div
          className="fixed z-[999] hidden min-w-[200px] rounded-lg border border-gray-100 bg-white py-2 shadow-lg lg:block"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
          onMouseEnter={() => setActiveDropdown(activeDropdown)}
          onMouseLeave={() => setActiveDropdown(null)}
        >
          {navItems
            .find(item => item.id === activeDropdown)
            ?.dropdownItems?.map(dropdownItem => (
              <button
                key={dropdownItem.id}
                onClick={() => handleNavClick(dropdownItem.id)}
                className={`navbar-link block w-full px-4 py-2.5 text-left text-[14px] transition-colors ${
                  currentPage === dropdownItem.id
                    ? "bg-[#FFF9F0] text-[#7A1E1E]"
                    : "text-gray-700 hover:bg-[#FFF5E6] hover:text-[#7A1E1E]"
                }`}
              >
                {dropdownItem.label}
              </button>
            ))}
        </div>
      )}

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="border-t border-gray-100 bg-white lg:hidden">
          <div className="max-h-[calc(100vh-80px)] space-y-1 overflow-y-auto px-4 py-3">
            {navItems.map(item => (
              <div key={item.id}>
                <button
                  onClick={() => {
                    if (item.hasDropdown) {
                      setActiveDropdown(
                        activeDropdown === item.id ? null : item.id
                      );
                    } else {
                      handleNavClick(item.id);
                    }
                  }}
                  className={`navbar-link flex w-full items-center justify-between rounded-lg px-4 py-3 text-left transition-colors ${
                    currentPage === item.id
                      ? "bg-[#FFF9F0] text-[#7A1E1E]"
                      : "text-gray-700 hover:bg-[#FFF5E6]"
                  }`}
                >
                  {item.label}
                  {item.hasDropdown && (
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        activeDropdown === item.id ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </button>

                {/* Mobile Dropdown Items */}
                {item.hasDropdown && activeDropdown === item.id && (
                  <div className="ml-4 mt-1 space-y-1">
                    {item.dropdownItems?.map(dropdownItem => (
                      <button
                        key={dropdownItem.id}
                        onClick={() => handleNavClick(dropdownItem.id)}
                        className={`navbar-link block w-full rounded-lg px-4 py-2.5 text-left text-sm transition-colors ${
                          currentPage === dropdownItem.id
                            ? "bg-[#FFF9F0] text-[#7A1E1E]"
                            : "text-gray-600 hover:bg-[#FFF5E6]"
                        }`}
                      >
                        {dropdownItem.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="mt-2 space-y-2 border-t border-gray-100 pt-3">
              <button
                onClick={() => setShowRegistrationModal(true)}
                className="navbar-link block w-full rounded-lg border-2 border-[#7A1E1E] px-4 py-3 text-center text-[#7A1E1E] transition-all hover:bg-[#7A1E1E] hover:text-white"
              >
                Register Institution
              </button>
              <button
                onClick={() => handleNavClick("donors")}
                className="navbar-link block w-full rounded-lg bg-gradient-to-r from-[#D4A017] to-[#B8900F] px-4 py-3 text-center text-white transition-all"
              >
                Donor Portal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hostel Registration Information Modal */}
      {showRegistrationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="relative max-h-[95vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-[#7A1E1E] px-4 py-3 sm:px-6 sm:py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-white sm:text-2xl">
                    Hostel Registration - Important Information
                  </h2>
                  <p className="mt-1 text-xs text-gray-200 sm:text-sm">
                    Please review the following requirements before proceeding.
                  </p>
                </div>
                <button
                  onClick={() => setShowRegistrationModal(false)}
                  className="rounded-lg p-2 text-white transition-colors hover:bg-white/20"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6">
              {/* Documents & Information Required */}
              <div className="mb-6 sm:mb-8">
                <div className="mb-4 flex items-center gap-2 sm:gap-3">
                  <FileText className="h-5 w-5 flex-shrink-0 text-[#7A1E1E] sm:h-6 sm:w-6" />
                  <h3 className="text-lg font-bold text-[#7A1E1E] sm:text-xl">
                    Documents & Information Required
                  </h3>
                </div>
                <p className="mb-4 text-sm text-gray-700 sm:mb-6 sm:text-base">
                  Please keep the following documents and information ready
                  before starting the registration process:
                </p>

                <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2">
                  {[
                    {
                      icon: <FileText className="h-6 w-6" />,
                      title: "Trust Registration Certificate",
                      description:
                        "Valid registration certificate of the trust/society.",
                    },
                    {
                      icon: <Users className="h-6 w-6" />,
                      title: "Current Occupancy Details",
                      description:
                        "Number of students, capacity, and facilities available.",
                    },
                    {
                      icon: <MapPin className="h-6 w-6" />,
                      title: "Location Information",
                      description: "Complete address with landmark details.",
                    },
                    {
                      icon: <Building2 className="h-6 w-6" />,
                      title: "Property Documents",
                      description:
                        "Ownership or lease documents of hostel premises.",
                    },
                    {
                      icon: <TrendingUp className="h-6 w-6" />,
                      title: "Financial Records",
                      description:
                        "Last 2 years financial statements (if applicable).",
                    },
                    {
                      icon: <Phone className="h-6 w-6" />,
                      title: "Contact Person Details",
                      description:
                        "Name, designation, phone, and email of authorized person.",
                    },
                  ].map(item => (
                    <div
                      key={item.title}
                      className="rounded-lg border border-gray-200 bg-[#FFF9F0] p-3 sm:p-4"
                    >
                      <div className="mb-2 flex items-start gap-2 sm:gap-3">
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#7A1E1E] text-white sm:h-10 sm:w-10">
                          {item.icon}
                        </div>
                        <h4 className="text-sm font-semibold text-[#7A1E1E] sm:text-base">
                          {item.title}
                        </h4>
                      </div>
                      <p className="text-xs text-gray-700 sm:text-sm">
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Important Guidelines */}
              <div className="mb-6 sm:mb-8">
                <div className="mb-4 flex items-center gap-2 sm:gap-3">
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#D4A017] sm:h-8 sm:w-8">
                    <AlertCircle className="h-4 w-4 text-white sm:h-5 sm:w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-[#7A1E1E] sm:text-xl">
                    Important Guidelines
                  </h3>
                </div>
                <ul className="space-y-2 sm:space-y-3">
                  {[
                    "Ensure all documents are clear and legible",
                    "Information provided should be accurate and up-to-date",
                    "You will receive confirmation within 3-5 business days",
                    "A JBF representative may contact you for verification",
                    "Incomplete applications will not be processed",
                  ].map(guideline => (
                    <li
                      key={guideline}
                      className="flex items-start gap-2 sm:gap-3"
                    >
                      <div className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-[#D4A017] sm:mt-1 sm:h-5 sm:w-5">
                        <CheckCircle className="h-2.5 w-2.5 text-white sm:h-3 sm:w-3" />
                      </div>
                      <span className="text-xs text-gray-700 sm:text-sm">
                        {guideline}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Need Help? */}
              <div className="mb-4 rounded-lg border border-gray-200 bg-[#FFF9F0] p-3 sm:mb-6 sm:p-4">
                <div className="mb-2 flex items-center gap-2 sm:gap-3">
                  <Mail className="h-4 w-4 flex-shrink-0 text-[#7A1E1E] sm:h-5 sm:w-5" />
                  <h3 className="text-sm font-semibold text-[#7A1E1E] sm:text-base">
                    Need Help?
                  </h3>
                </div>
                <p className="text-xs text-gray-700 sm:text-sm">
                  If you have any questions about the registration process,
                  please contact us at{" "}
                  <a
                    href="mailto:registration@jbfindia.org"
                    className="break-all text-[#7A1E1E] underline hover:text-[#5A1515]"
                  >
                    registration@jbfindia.org
                  </a>{" "}
                  or call +91 XXXXX XXXXX.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  onClick={() => setShowRegistrationModal(false)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 sm:w-auto sm:px-6 sm:py-3"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProceedToForm}
                  className="w-full rounded-lg bg-[#7A1E1E] px-4 py-2.5 text-sm text-white transition-colors hover:bg-[#5A1515] sm:w-auto sm:px-6 sm:py-3"
                >
                  I&apos;m Ready - Proceed to Registration
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hostel Registration Form Modal */}
      {showRegistrationForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="relative max-h-[95vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-[#FFF9F0] shadow-2xl">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white px-4 py-3 shadow-sm sm:px-6 sm:py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#7A1E1E] sm:h-10 sm:w-10">
                    <Building2 className="h-5 w-5 text-white sm:h-6 sm:w-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-[#7A1E1E] sm:text-2xl">
                      Hostel Registration
                    </h2>
                    <p className="text-xs text-gray-600 sm:text-sm">
                      Submit an expression of interest
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowRegistrationForm(false)}
                  className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleFormSubmit} className="p-4 sm:p-6">
              <div className="space-y-4 sm:space-y-6">
                {/* Hostel Name */}
                <div>
                  <label
                    htmlFor="hostelName"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Hostel Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="hostelName"
                    name="hostelName"
                    value={hostelFormData.hostelName}
                    onChange={handleHostelFormChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                    placeholder="Enter hostel name"
                  />
                </div>

                {/* Trust Name */}
                <div>
                  <label
                    htmlFor="trustName"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Trust/Society Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="trustName"
                    name="trustName"
                    value={hostelFormData.trustName}
                    onChange={handleHostelFormChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                    placeholder="Enter trust/society name"
                  />
                </div>

                {/* Address */}
                <div>
                  <label
                    htmlFor="address"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="address"
                    name="address"
                    value={hostelFormData.address}
                    onChange={handleHostelFormChange}
                    required
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                    placeholder="Enter complete address"
                  />
                </div>

                {/* City, State, Pincode */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label
                      htmlFor="city"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={hostelFormData.city}
                      onChange={handleHostelFormChange}
                      required
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="state"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      State <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={hostelFormData.state}
                      onChange={handleHostelFormChange}
                      required
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                      placeholder="State"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="pincode"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      Pincode <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="pincode"
                      name="pincode"
                      value={hostelFormData.pincode}
                      onChange={handleHostelFormChange}
                      required
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                      placeholder="Pincode"
                    />
                  </div>
                </div>

                {/* Contact Person */}
                <div>
                  <label
                    htmlFor="contactPerson"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Contact Person Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contactPerson"
                    name="contactPerson"
                    value={hostelFormData.contactPerson}
                    onChange={handleHostelFormChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                    placeholder="Enter contact person name"
                  />
                </div>

                {/* Phone and Email */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="phone"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={hostelFormData.phone}
                      onChange={handleHostelFormChange}
                      required
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={hostelFormData.email}
                      onChange={handleHostelFormChange}
                      required
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                      placeholder="Enter email address"
                    />
                  </div>
                </div>

                {/* Capacity and Current Occupancy */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="capacity"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      Total Capacity <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="capacity"
                      name="capacity"
                      value={hostelFormData.capacity}
                      onChange={handleHostelFormChange}
                      required
                      min="1"
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                      placeholder="Enter total capacity"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="currentOccupancy"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      Current Occupancy <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="currentOccupancy"
                      name="currentOccupancy"
                      value={hostelFormData.currentOccupancy}
                      onChange={handleHostelFormChange}
                      required
                      min="0"
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                      placeholder="Enter current occupancy"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-center pt-2 sm:pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#7A1E1E] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#5A1515] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-8 sm:py-3 sm:text-base"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Registration
                        <Send className="h-4 w-4 sm:h-5 sm:w-5" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </nav>
  );
}
